<script setup lang="ts">
  import { LayoutDisplayConfig } from '@/modules/display/composables/Calls.api'
  import { computed, onBeforeUnmount, onMounted, PropType, ref, watch } from 'vue'
  import Call from './../Call.vue'
  import { CallWithRelations } from '@/store/call.state'
  import { DisplayProfile, Locale } from '@/common/graphql/types'
  import Presence from '@/modules/display/components/Presence.vue'
  import { useStore } from '@/store'
  import { useWindowSize } from '@vueuse/core'
  import { useRouter } from 'vue-router'

  defineEmits(['close', 'click'])
  const props = defineProps({
    calls: {
      type: Array as PropType<CallWithRelations[]>,
      default: () => ([])
    },
    presences: {
      type: Array as PropType<CallWithRelations[]>,
      default: () => ([])
    },
    displayConfig: {
      type: Object as PropType<LayoutDisplayConfig>,
      required: true,
    },
    displayProfile: {
      type: Object as PropType<DisplayProfile>,
      default: () => ({})
    },
    fontSize: {
      type: String,
      default: () => 'normal'
    },
    locale: {
      type: String,
      default: Locale.De
    },
  })

  const router = useRouter()
  const store = useStore()
  const { width: windowWidth, height: windowHeight } = useWindowSize()

  const gridGap = 10
  const isVertical = computed(() => windowWidth.value < windowHeight.value)
  const isFullscreen = computed(() => store.state.app.isFullscreen)
  const presenceColumns = computed(() => isVertical.value ? 1 : 2)

  const containerHeight = computed(() => {
    if (isFullscreen.value) {
      return window.innerHeight - 80 // -padding top/bottom
    } else {
      return window.innerHeight - headerHeight.value - footerHeight.value - topBarHeight.value
    }
  })

  // Calculate how many presences can be displayed vertically in the container.
  const maxPresencesVertically = computed(() => Math.floor(containerHeight.value / presenceElementHeight.value))

  // never show more than this number of presences.
  const maxPresences = computed(() => maxPresencesVertically.value > 0 ? maxPresencesVertically.value * presenceColumns.value : 20)

  // If there are more presences than maxPresences, show the overflow button.
  const overflowPresencesVisible = ref(false)
  const visiblePresences = computed(() => {
    if (overflowPresencesVisible.value) {
      return props.presences
    }
    return props.presences.slice(0, presencesOverflowCount.value > 0 ? maxPresences.value - 1 : maxPresences.value)
  })
  const presencesOverflowCount = computed(() => {
    if (props.presences.length <= maxPresences.value || overflowPresencesVisible.value) {
      return 0
    }
    return props.presences.length - maxPresences.value + 1
  })

  const presenceElements = ref<HTMLElement[]>([])
  const presenceElementHeight = computed(() => {
    const presenceElement = presenceElements.value[0] ?? null
    if (!presenceElement) {
      return 200
    }

    return presenceElement.getBoundingClientRect().height + gridGap
  })

  const headerHeight = ref(0)
  const footerHeight = ref(0)
  const topBarHeight = ref(0)

  // Automatically hide the overflow presences after a few seconds.
  let overflowHideTimeout: number | undefined
  watch(() => overflowPresencesVisible.value, (newValue) => {
    window.clearTimeout(overflowHideTimeout)
    if (newValue) {
      overflowHideTimeout = window.setTimeout(() => overflowPresencesVisible.value = false, 60_000)
    }
  })

  onMounted(() => {
    headerHeight.value = document.querySelector('#main-header')?.getBoundingClientRect().height ?? 0
    footerHeight.value = document.querySelector('#main-footer')?.getBoundingClientRect().height ?? 0
    topBarHeight.value = document.querySelector('#main-topbar')?.getBoundingClientRect().height ?? 0
  })

  onBeforeUnmount(() => {
    window.clearTimeout(overflowHideTimeout)
  })

  const userIsLoggedIn = computed(() => store.state.user.user.id > 0)
  const isMultiSystemContext = computed(() => props.displayProfile?.settings?.length > 1)

  function onClick (call: CallWithRelations) {
    if (userIsLoggedIn.value) {
      // In case of multi-system context, open the call in a new window.
      // It is too complicated to handle the multi-system context in the call form.
      if (isMultiSystemContext.value) {
        window.open(`${router.resolve({
          name: 'callForm',
          params: { id: call.id }
        }).href}?system=${call.system_id}`, '_blank')
      } else {
        router.push({
          name: 'displayCallForm',
          params: { id: call.id, slug: router.currentRoute.value.params.slug || '' }
        })
      }
    }
  }
</script>

<template>
  <div
    class="grid select-none"
    :class="{
      'grid--fullscreen': isFullscreen,
      'grid--vertical': isVertical,
    }"
    @click="$emit('click', $event)"
  >
    <div class="calls">
      <div class="call-grid" :class="{'call-grid--low-volume': props.calls.length === 1 || (props.calls.length < 3 && visiblePresences.length <= maxPresencesVertically) }">
        <Call
          v-for="call in calls"
          :key="call.id"
          :call="call"
          :displayConfig="displayConfig.calls"
          :displayProfile="displayProfile"
          :locale="locale"
          @click="onClick($event)"
          @close="$emit('close', $event)"
        />
      </div>
    </div>
    <div
      v-if="visiblePresences.length"
      class="presences"
      :class="{'presences--two-cols': visiblePresences.length > maxPresencesVertically}"
    >
      <div class="call-grid">
        <div v-for="call in visiblePresences" :key="call.id" ref="presenceElements">
          <Presence
            :call="call"
            :displayConfig="displayConfig.presences"
            :displayProfile="displayProfile"
            :locale="locale"
            @click="onClick($event)"
            @close="$emit('close', $event)"
          />
        </div>
        <div
          v-if="presencesOverflowCount > 0 && !overflowPresencesVisible"
          class="border-2 border-gray-300 border-dashed rounded-lg p-2 flex items-center justify-center text-sm cursor-pointer hover:bg-gray-200"
          @click.prevent="overflowPresencesVisible = true"
        >
          {{ $t('call.display.presences_overflow', { count: presencesOverflowCount }) }}
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="stylus" scoped>
  .grid
    display flex
    height 100%
    z-index 30
    position relative
    gap 20px

    @media screen and (max-width: 670px)
      flex-direction column

  .call-grid
    display grid
    grid-gap 10px
    z-index 10

  .calls
    flex 1 1 auto
    --call-width: 340px

    .grid--fullscreen &
      --call-width: 400px

    .grid--fullscreen.grid--vertical &
      --call-width: 340px

    .call-grid
      grid-template-columns repeat(auto-fit, minmax(var(--call-width), 1fr))
      grid-auto-rows min-content

      &--low-volume
        grid-template-columns repeat(auto-fit, calc(100% / 3 - 7px))

        .grid--vertical &,
        .visuals-display--with-notes &
          grid-template-columns repeat(auto-fit, calc(100% / 2 - 5px))

  .presences
    flex 0 0 var(--presence-col-width)
    min-width 0
    --presence-col-width: 230px

    @media screen and (max-width: 670px)
      --presence-col-width 100%;

    .call-grid
      grid-template-columns var(--presence-col-width)
      grid-auto-flow row

    &--two-cols
      flex 0 0 calc(var(--presence-col-width) * 2)

      .call-grid
        grid-template-columns repeat(auto-fill, minmax(calc(100% / 2 - 10px), 1fr))
</style>